"use client";

import { FormEvent, useRef, useState } from "react";
import { CalendarCheck2, Download, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

type Props = { slug: string; title: string; price: number; merchant: string; address: string };

export const FlashSaleBooking = ({ slug, title, price, merchant, address }: Props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<{ ticketCode: string; totalAmount: number } | null>(null);
  const qrRef = useRef<HTMLDivElement>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    const data = new FormData(event.currentTarget);
    const response = await fetch(`/api/flash-sales/${slug}/book`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(Object.fromEntries(data)),
    });
    const result = await response.json();
    setLoading(false);
    if (!response.ok) return setError(result.error ?? "No se pudo reservar");
    setTicket(result);
  };

  const download = () => {
    if (!ticket) return;
    const svg = qrRef.current?.querySelector("svg")?.outerHTML ?? "";
    const html = `<!doctype html><meta charset="utf-8"><title>${ticket.ticketCode}</title><style>body{font-family:Arial;max-width:560px;margin:48px auto;color:#0f172a}.ticket{border:2px solid #0c4a6e;border-radius:24px;padding:32px}h1{font-size:28px}.code{font-size:20px;font-weight:700;letter-spacing:2px}.small{color:#475569}</style><div class="ticket"><p>PORT DE PREMIÀ · EXPERIENCIAS</p><h1>${title}</h1><p>${merchant}<br>${address}</p>${svg}<p class="code">${ticket.ticketCode}</p><p>Total a pagar en el establecimiento: ${ticket.totalAmount.toFixed(2)} €</p><p class="small">Presenta este QR al consumir la experiencia.</p></div>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `ticket-${ticket.ticketCode}.html`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (ticket) {
    return (
      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6 text-center">
        <p className="text-xs font-bold tracking-widest text-emerald-700 uppercase">Reserva registrada</p>
        <h2 className="mt-2 text-2xl font-extrabold">Reserva confirmada</h2>
        <p className="mt-3 text-sm text-slate-600">Presenta el ticket al consumir la experiencia y paga <strong>{ticket.totalAmount.toFixed(2)} €</strong> directamente en el establecimiento.</p>
        <div ref={qrRef} className="mx-auto mt-5 w-fit rounded-2xl bg-white p-4"><QRCodeSVG value={`https://portdepremia.com/experiencias/ticket/${ticket.ticketCode}`} size={180} level="H" /></div>
        <p className="mt-3 font-mono font-bold tracking-wider">{ticket.ticketCode}</p>
        <button onClick={download} className="mt-5 inline-flex h-11 items-center gap-2 rounded-xl bg-sky-950 px-5 text-sm font-bold text-white"><Download className="size-4" /> Descargar ticket</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/5">
      <p className="text-xs font-bold tracking-widest text-amber-600 uppercase">Reserva inmediata</p>
      <h2 className="mt-2 text-3xl font-extrabold">{price.toFixed(2)} €</h2>
      <div className="mt-5 grid gap-4">
        <input required name="customerName" placeholder="Nombre y apellidos" className="h-11 rounded-xl border border-slate-200 px-3" />
        <input required type="email" name="customerEmail" placeholder="Correo electrónico" className="h-11 rounded-xl border border-slate-200 px-3" />
        <input name="customerPhone" placeholder="Teléfono" className="h-11 rounded-xl border border-slate-200 px-3" />
        <label className="text-sm font-medium">Cantidad<input name="quantity" type="number" min="1" max="10" defaultValue="1" className="ml-3 h-10 w-20 rounded-lg border border-slate-200 px-3" /></label>
      </div>
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <button disabled={loading} className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-sky-950 px-5 font-bold text-white disabled:opacity-60">
        {loading ? <Loader2 className="size-4 animate-spin" /> : <CalendarCheck2 className="size-4" />} Reservar experiencia
      </button>
      <p className="mt-3 text-xs text-slate-500">No pagas ahora. El importe se abona en el establecimiento al consumir el servicio.</p>
    </form>
  );
};
